import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import MainLayout from './components/layout/MainLayout';
import AppEntry from './views/AppEntry';
import PlayTabView from './views/PlayTabView';
// import ContestsView from './views/ContestsView'; // Commented out - hidden for now
import MyPicksView from './views/MyPicksView';
import ResultsView from './views/ResultsView';
import ContestRulesRedirect from './components/rules/ContestRulesRedirect';
import HelpView from './views/HelpView';
import { OAuthCallback } from './components/auth/OAuthCallback';
import { apiClient } from './api/api-client';
import { useGetTenant } from './hooks/useTenant';
import { getGAMConfigFromTenant } from './config/ads.config';
import { initializeGPT, enableAndDisplayAds } from './utils/gpt';
import { startAdRenderReporting } from './utils/adReporting';
import './App.css';

import {
  resolveTenantFromUrl,
  getTenantBasePath,
  isUrlTenantMode,
  isClientGameEnabled,
  GameEntry,
} from '@low6-pickem/frontend-shared';
import { ClientGameGate } from '@low6-pickem/ui';

const TENANT_SLUG = resolveTenantFromUrl();
const TENANT_BASE_PATH = getTenantBasePath();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Tenant and GPT initialization wrapper
 * Fetches tenant config, initializes GAM if enabled, provides TenantProvider
 */
function TenantAndAdsWrapper({ children }: { children: React.ReactNode }) {
  const { data: tenant, isLoading: isTenantLoading } = useGetTenant(TENANT_SLUG);

  useEffect(() => {
    if (isTenantLoading || !tenant) return;
    const gamConfig = getGAMConfigFromTenant(tenant);
    if (!gamConfig?.enabled) return;

    /*
     * Subscribe before GPT is asked to fill anything. `slotRenderEnded` fires once per
     * slot per request, so a listener attached after the first fill misses it outright
     * — and the first fill is the one that matters, since that is what a player sees
     * and screenshots. Safe this early: the subscription queues through googletag.cmd.
     */
    const stopReporting = startAdRenderReporting();

    const initAds = async () => {
      try {
        await initializeGPT();
        enableAndDisplayAds();
      } catch (err) {
        console.error('[Squares] Failed to initialize GPT:', err);
      }
    };
    initAds();

    return stopReporting;
  }, [tenant, isTenantLoading]);

  // If URL tenant mode and tenant fetch failed, redirect to default
  if (!isTenantLoading && !tenant && isUrlTenantMode()) {
    window.location.href = '/';
    return null;
  }

  // Use fallback tenant when fetch fails so app still works (ads disabled)
  const effectiveTenant = tenant || {
    id: '',
    name: '',
    code: TENANT_SLUG,
    gamEnabled: false,
    gamNetworkId: null,
  };

  return <TenantProvider tenant={effectiveTenant}>{children}</TenantProvider>;
}

/**
 * App Content Component
 * Handles authentication check and routing
 *
 * Note: Unlike having a login screen, this app allows unauthenticated access.
 * When API calls return 401, the api-client interceptor will auto-trigger OAuth.
 */
function AppContent() {
  const { isLoading, authError } = useAuth();

  const isOAuthCallback = window.location.pathname.endsWith('/auth/callback');

  // Trigger authentication check on mount by calling an auth-required endpoint
  // This will return 401 if not authenticated, triggering the OAuth flow via api-client interceptor
  useEffect(() => {
    if (isOAuthCallback || isLoading) return;

    // Call an authenticated endpoint to verify auth status
    // If not authenticated, this triggers 401 → OAuth flow
    const checkAuth = async () => {
      try {
        await apiClient.get('/api/squares/user/my-picks');
      } catch (_error) {
        // 401 errors are handled by the api-client interceptor (triggers OAuth)
        // Other errors don't block the app
      }
    };

    checkAuth();
  }, [isOAuthCallback, isLoading]);

  if (isOAuthCallback) {
    return (
      <BrowserRouter basename={TENANT_BASE_PATH || undefined}>
        <Routes>
          <Route path="/auth/callback" element={<OAuthCallback />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#121212]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#53d337] mx-auto mb-4"></div>
          <p className="text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  // Show error message if authentication failed
  if (authError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#121212]">
        <div className="text-center max-w-md px-6">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2
            className="text-white text-xl font-bold mb-2"
            style={{ fontFamily: '"Saira Extra Condensed", sans-serif' }}
          >
            Authentication Error
          </h2>
          <p className="text-gray-400 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
            {authError.message}
          </p>
        </div>
      </div>
    );
  }

  // Show app - if user is not authenticated, API calls will return 401
  // and the api-client interceptor will auto-trigger OAuth login
  return (
    <TenantAndAdsWrapper>
      <BrowserRouter basename={TENANT_BASE_PATH || undefined}>
        <Routes>
          {/* OAuth callback route - must be outside MainLayout */}
          <Route path="/auth/callback" element={<OAuthCallback />} />

          {/* App entry - RulesView or redirect (no MainLayout/tabs).
              DK iframe entry (DKPLAT-467): /freetoplay?gameId=&tenantId= and
              proxy-stripped /?gameId= both deep-link to /game/:gameId */}
          <Route
            path="/"
            element={
              <GameEntry>
                <AppEntry />
              </GameEntry>
            }
          />
          <Route
            path="/freetoplay"
            element={
              <GameEntry>
                <AppEntry />
              </GameEntry>
            }
          />
          {/* Deep-link to a specific game by id */}
          <Route path="/game/:gameId" element={<AppEntry />} />

          {/* Main app routes - Play, My Games, Recent */}
          <Route path="/" element={<MainLayout />}>
            <Route path="play" element={<PlayTabView />} />
            {/* <Route path="contests" element={<ContestsView />} /> */}
            <Route path="my-picks" element={<MyPicksView />} />
            <Route path="results" element={<ResultsView />} />
          </Route>

          {/* Deep link: redirect to PlayView */}
          <Route path="/contest/:contestId/rules" element={<ContestRulesRedirect />} />

          {/* Help view - outside MainLayout (no tabs) */}
          <Route path="/help" element={<HelpView />} />
        </Routes>
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </TenantAndAdsWrapper>
  );
}

function App() {
  // Layer A: don't register this game's routes if the client didn't deploy it.
  return (
    <ClientGameGate enabled={isClientGameEnabled('squares')}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </QueryClientProvider>
    </ClientGameGate>
  );
}

export default App;
